import { useEffect, useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import FocusLock from 'react-focus-lock'
import { IoBookOutline, IoChatbubblesOutline, IoChevronForward, IoEllipsisHorizontal, IoFlameOutline, IoGiftOutline, IoLogOut, IoMailOutline, IoMicOutline, IoMoon, IoPeopleOutline, IoPersonOutline, IoPhonePortraitOutline, IoSchoolOutline, IoSettingsOutline, IoSunny } from 'react-icons/io5'
import { HebrewTranslateIcon } from '@/components/icons/hebrew-translate-icon'
import { clearAuthToken, getAuthEmail } from '@/lib/baba-auth'
import { clearAccountLocalState } from '@/lib/accountLocalState'
import { phReset } from '@/lib/posthog'
import { useTheme } from '../theme-provider'
import { useSignIn } from './SignInModal'
import Rail from './Rail'
import { cn } from '@/lib/utils'
import { isSubscriptionActive } from '@/lib/baba-api'
import { useSubscriptionStatusQuery } from '../store/babaApi'
import { useUsageSnapshot } from './PlanMeter'
import { useEmailGate } from './email/EmailGate'

export { useUsageSnapshot } from './PlanMeter'

// The Hebrew aleph mark, shared with the desktop rail and the translate
// history rows so every Translate affordance carries the same glyph.
const TranslateIcon = <HebrewTranslateIcon size={22} />

const ChatIcon = (
  <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
  </svg>
)

const LibraryIcon = (
  // Stack of books — unambiguously a library
  <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <rect x="4" y="4" width="4" height="16" rx="1" />
    <rect x="10" y="4" width="4" height="16" rx="1" />
    <path d="M17 5.5l3 0.7-3 14-3-0.7z" />
  </svg>
)

const SettingsIcon = <IoSettingsOutline size={19} />

// Mobile bottom tab bar — matches the desktop rail's Translate / Chat /
// Library order, plus Settings (the rail keeps Settings as an icon too, but
// mobile has no account popover to tuck it into).
const MOBILE_NAV: Array<{ to: string; label: string; icon: React.ReactNode; match: (pathname: string) => boolean }> = [
  { to: '/translate', label: 'Translate', icon: TranslateIcon, match: (p) => p.startsWith('/translate') },
  { to: '/chat/x', label: 'Chat', icon: ChatIcon, match: (p) => p.startsWith('/chat') },
  { to: '/library', label: 'Library', icon: LibraryIcon, match: (p) => p.startsWith('/library') },
]

/** Everything else the phone drawer holds on mobile lives behind "More". */
const MORE_NAV: Array<{ to: string; label: string; icon: React.ReactNode; match: (pathname: string) => boolean }> = [
  // baba Email: listed here so the More tab lights up on /email; the tile
  // itself only renders for an enabled account (see MobileTabBar).
  { to: '/email', label: 'Email', icon: <IoMailOutline size={18} />, match: (p) => p.startsWith('/email') },
  { to: '/tutor', label: 'Voice Tutor', icon: <IoMicOutline size={18} />, match: (p) => p.startsWith('/tutor') },
  { to: '/warm-up', label: 'Warm-up', icon: <IoMicOutline size={18} />, match: (p) => p.startsWith('/warm-up') },
  { to: '/conversation', label: 'Conversation', icon: <IoPeopleOutline size={18} />, match: (p) => p.startsWith('/conversation') },
  { to: '/dictionary', label: 'Slang Dictionary', icon: <IoBookOutline size={18} />, match: (p) => p.startsWith('/dictionary') },
  { to: '/profile/invite', label: 'Invite Friends', icon: <IoGiftOutline size={18} />, match: (p) => p.startsWith('/profile/invite') },
  { to: '/learn', label: 'Learn (Pre)', icon: <IoSchoolOutline size={18} />, match: (p) => p.startsWith('/learn') },
  // Last, to match the desktop rail — a scheduled WhatsApp drip, not a surface
  // anyone opens mid-task.
  { to: '/situations', label: 'Situations', icon: <IoChatbubblesOutline size={18} />, match: (p) => p.startsWith('/situations') },
  { to: '/profile', label: 'Profile', icon: <IoPersonOutline size={18} />, match: (p) => p.startsWith('/profile') && !p.startsWith('/profile/invite') },
  { to: '/settings', label: 'Settings', icon: SettingsIcon, match: (p) => p.startsWith('/settings') || p.startsWith('/account') },
]

/**
 * Sign out keeps the user in the app, signed out. Every sign-out path goes
 * through here: the chats, recents, progress and caches this browser kept for
 * the account go with the session, so the next person to sign in here neither
 * sees them nor uploads them into their own account (lib/accountLocalState.ts).
 */
function signOut() {
  phReset()
  clearAccountLocalState()
  clearAuthToken()
  window.location.href = '/'
}

export function SignOutConfirm({ onCancel }: { onCancel: () => void }) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center animate-fade-in backdrop-blur-sm"
      style={{ background: 'var(--overlay)' }}
      onClick={onCancel}
    >
      <FocusLock returnFocus autoFocus>
        <div
          className="rounded-3xl border border-app bg-surface p-6 shadow-token-lg w-[340px] animate-scale-in"
          onClick={(e) => e.stopPropagation()}
          role="alertdialog"
          aria-modal="true"
          aria-labelledby="signout-title"
        >
          <h3 id="signout-title" className="text-base font-semibold text-app mb-2">
            Are you sure you want to log out?
          </h3>
          <div className="flex items-center gap-3 mt-5">
            <button
              type="button"
              onClick={onCancel}
              className="flex-1 h-10 rounded-full text-sm font-medium text-muted bg-surface-2 border border-app hover:bg-[var(--card-2)] hover:text-[var(--ink)] active:scale-95 transition-colors cursor-pointer"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={signOut}
              className="flex-1 h-10 rounded-full text-sm font-semibold text-white bg-[var(--danger)] hover:brightness-110 shadow-sm transition-colors active:scale-95 cursor-pointer"
            >
              Log Out
            </button>
          </div>
        </div>
      </FocusLock>
    </div>
  )
}

/**
 * Account card at the top of the More sheet: who is signed in and on which
 * plan, with one button to Settings (or to sign in). Guests see the free
 * counter so the sheet still explains what the limit is.
 */
function MoreAccountCard({ email, onSettings, onProfile, onSignIn }: { email: string | null; onSettings: () => void; onProfile: () => void; onSignIn: () => void }) {
  const signedIn = !!email
  const { data: subscription } = useSubscriptionStatusQuery(undefined, { skip: !signedIn })
  const isPro = subscription ? isSubscriptionActive(subscription) : false
  const isProPlus = isPro && subscription?.tier === 'pro_plus'
  const { limit, left } = useUsageSnapshot(signedIn)
  const planLine = isProPlus ? 'Pro+' : isPro ? 'Pro' : `${signedIn ? 'Free' : 'Guest'} · ${left.toLocaleString()} of ${limit.toLocaleString()} left`
  const initial = (email?.[0] ?? 'b').toUpperCase()
  return (
    <div className="flex items-center gap-3 shrink-0 rounded-md" style={{ padding: '12px 14px', background: 'var(--card)', border: '1px solid var(--line)' }}>
      <span
        className="flex items-center justify-center shrink-0 rounded-full text-[15px] font-semibold"
        style={{ width: 40, height: 40, background: 'var(--accent-soft)', color: 'var(--accent-deep)' }}
        aria-hidden
      >
        {signedIn ? initial : <IoPersonOutline size={18} />}
      </span>
      <button
        type="button"
        onClick={signedIn ? onProfile : onSignIn}
        className="flex flex-col gap-0.5 flex-1 min-w-0 text-left cursor-pointer"
      >
        <span className="text-[14px] font-semibold truncate" style={{ color: 'var(--ink)' }}>{email ?? 'Not Signed In'}</span>
        <span className="font-mono text-[11px] leading-snug" style={{ color: 'var(--muted-2)' }}>{signedIn ? `${planLine} · Profile` : planLine}</span>
      </button>
      {signedIn ? (
        <button
          type="button"
          onClick={onSettings}
          className="touch-44-y shrink-0 inline-flex items-center rounded-md text-[13px] font-semibold cursor-pointer"
          style={{ height: 36, padding: '0 12px', border: '1px solid var(--line)', background: 'var(--card)', color: 'var(--ink)' }}
        >
          Settings
        </button>
      ) : (
        <button
          type="button"
          onClick={onSignIn}
          className="touch-44-y shrink-0 inline-flex items-center rounded-md text-[13px] font-semibold cursor-pointer"
          style={{ height: 36, padding: '0 12px', border: '1px solid var(--accent-deep)', background: 'var(--accent)', color: 'var(--cta-fg)' }}
        >
          Sign In Free
        </button>
      )}
    </div>
  )
}

/** The six feature tiles in the More sheet: what each one is, in one line. */
const MORE_TILES: Array<{ to: string; label: string; note: string; icon: React.ReactNode; match: (pathname: string) => boolean }> = [
  { to: '/tutor', label: 'Voice Tutor', note: 'Talk with Avi, get corrected', icon: <IoMicOutline size={20} />, match: (p) => p.startsWith('/tutor') },
  { to: '/conversation', label: 'Conversation', note: 'Two-way live interpreter', icon: <IoPeopleOutline size={20} />, match: (p) => p.startsWith('/conversation') },
  { to: '/warm-up', label: 'Warm-up', note: 'Two minutes of speaking', icon: <IoFlameOutline size={20} />, match: (p) => p.startsWith('/warm-up') },
  { to: '/dictionary', label: 'Slang Dictionary', note: 'The words people actually say', icon: <IoBookOutline size={20} />, match: (p) => p.startsWith('/dictionary') },
  { to: '/situations', label: 'Situations', note: 'One WhatsApp scene a day', icon: <IoChatbubblesOutline size={20} />, match: (p) => p.startsWith('/situations') },
  { to: '/learn', label: 'Learn', note: 'The course, coming soon', icon: <IoSchoolOutline size={20} />, match: (p) => p.startsWith('/learn') },
]

/** baba Email, appended after the other tiles only when the account is enabled. */
const EMAIL_TILE: (typeof MORE_TILES)[number] = { to: '/email', label: 'Email', note: 'Forward a Hebrew email, read it in English', icon: <IoMailOutline size={20} />, match: (p) => p.startsWith('/email') }

const sheetRowClass = 'flex shrink-0 items-center gap-3 px-4 text-[15px] font-medium text-left transition-colors duration-150 cursor-pointer active:bg-[var(--card-2)]'
const sheetRowStyle: React.CSSProperties = { minHeight: 52, color: 'var(--ink)' }
const sheetDivider: React.CSSProperties = { borderTop: '1px solid var(--line-hair, var(--line))' }

/** Bottom tab bar — the entire mobile navigation. No hamburger, no drawer. */
export function MobileTabBar() {
  const { pathname } = useLocation()
  const navigate = useNavigate()
  const [moreOpen, setMoreOpen] = useState(false)
  const [showSignOutConfirm, setShowSignOutConfirm] = useState(false)
  const { resolvedTheme, toggle: toggleTheme } = useTheme()
  const { open: openSignIn } = useSignIn()
  const { enabled: emailEnabled } = useEmailGate()
  const moreActive = MORE_NAV.some((item) => item.match(pathname) && (item.to !== '/email' || emailEnabled))
  // Appended, not prepended: a tile that arrives with the gate's answer must
  // not push every other tile down the sheet under the user's thumb.
  const tiles = emailEnabled ? [...MORE_TILES, EMAIL_TILE] : MORE_TILES
  const email = getAuthEmail()
  const dark = resolvedTheme === 'dark'

  useEffect(() => {
    setMoreOpen(false)
  }, [pathname])

  return (
    <nav
      aria-label="Main Navigation"
      className="md:hidden shrink-0 flex items-stretch border-t border-app"
      style={{ background: 'var(--cream-2)', paddingBottom: 'env(safe-area-inset-bottom)' }}
    >
      {moreOpen && (
        <div className="fixed inset-0 z-[9000] flex flex-col justify-end" role="dialog" aria-modal="true" aria-label="More">
          <div className="absolute inset-0" style={{ background: 'var(--overlay-backdrop)' }} onClick={() => setMoreOpen(false)} />
          <FocusLock returnFocus autoFocus>
            {/*
             * Everything the desktop rail carries that has no tab of its own:
             * the account, the six secondary features as tiles, then the
             * theme toggle, the store links and sign in / sign out. Capped at
             * 82dvh and scrollable so a small screen still reaches the last
             * row.
             */}
            <div
              className="relative flex flex-col gap-4 sheet-max-h overflow-y-auto animate-fade-up"
              style={{ background: 'var(--cream)', borderRadius: '14px 14px 0 0', boxShadow: 'var(--shadow-lg)', padding: '8px 16px', paddingBottom: 'calc(20px + env(safe-area-inset-bottom))' }}
            >
              <div className="mx-auto shrink-0" style={{ width: 36, height: 4, borderRadius: 2, background: 'var(--line)' }} />

              <MoreAccountCard
                email={email}
                onSettings={() => {
                  setMoreOpen(false)
                  navigate('/settings')
                }}
                onProfile={() => {
                  setMoreOpen(false)
                  navigate('/profile')
                }}
                onSignIn={() => {
                  setMoreOpen(false)
                  openSignIn('sidebar')
                }}
              />

              <div className="grid shrink-0" style={{ gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 10 }}>
                {tiles.map((tile) => {
                  const active = tile.match(pathname)
                  return (
                    <Link
                      key={tile.to}
                      to={tile.to}
                      aria-current={active ? 'page' : undefined}
                      className="flex flex-col gap-2.5 rounded-md transition-colors duration-150 cursor-pointer active:bg-[var(--card-2)]"
                      style={{
                        padding: 14,
                        minHeight: 92,
                        background: active ? 'var(--accent-soft)' : 'var(--card)',
                        border: `1px solid ${active ? 'var(--accent)' : 'var(--line)'}`,
                      }}
                    >
                      {/* Icons stay ink: terracotta marks the active tile only. */}
                      <span style={{ color: active ? 'var(--accent-deep)' : 'var(--ink)' }}>{tile.icon}</span>
                      <span className="flex flex-col gap-0.5">
                        <span className="text-[14px] font-semibold" style={{ color: 'var(--ink)' }}>{tile.label}</span>
                        <span className="text-[12px] leading-[1.35]" style={{ color: 'var(--muted-2)' }}>{tile.note}</span>
                      </span>
                    </Link>
                  )
                })}
              </div>

              <div className="flex flex-col shrink-0 rounded-md overflow-hidden" style={{ background: 'var(--card)', border: '1px solid var(--line)' }}>
                <Link to="/profile/invite" className={sheetRowClass} style={sheetRowStyle}>
                  <IoGiftOutline size={20} style={{ color: 'var(--muted)' }} />
                  <span className="flex-1">Invite Friends</span>
                  <IoChevronForward size={16} style={{ color: 'var(--line-2)' }} aria-hidden />
                </Link>
                <button
                  type="button"
                  role="switch"
                  aria-checked={dark}
                  onClick={toggleTheme}
                  className={sheetRowClass}
                  style={{ ...sheetRowStyle, ...sheetDivider }}
                >
                  {dark ? <IoSunny size={20} style={{ color: 'var(--muted)' }} /> : <IoMoon size={20} style={{ color: 'var(--muted)' }} />}
                  <span className="flex-1">Dark Mode</span>
                  <span
                    className="relative shrink-0 rounded-full transition-colors duration-150"
                    style={{ width: 44, height: 26, background: dark ? 'var(--accent)' : 'var(--line)' }}
                    aria-hidden
                  >
                    <span
                      className="absolute rounded-full transition-transform duration-150"
                      style={{ top: 3, left: 3, width: 20, height: 20, background: 'var(--card)', transform: dark ? 'translateX(18px)' : 'none', boxShadow: 'var(--shadow-sm)' }}
                    />
                  </span>
                </button>
                <Link to="/settings/support" className={sheetRowClass} style={{ ...sheetRowStyle, ...sheetDivider }}>
                  <IoPhonePortraitOutline size={20} style={{ color: 'var(--muted)' }} />
                  <span className="flex-1">Get the iPhone or Android App</span>
                  <IoChevronForward size={16} style={{ color: 'var(--line-2)' }} aria-hidden />
                </Link>
                {email && (
                  <button
                    type="button"
                    onClick={() => {
                      setMoreOpen(false)
                      setShowSignOutConfirm(true)
                    }}
                    className={sheetRowClass}
                    style={{ ...sheetRowStyle, ...sheetDivider }}
                  >
                    <IoLogOut size={20} style={{ color: 'var(--muted)' }} />
                    <span className="flex-1">Log Out</span>
                  </button>
                )}
              </div>
            </div>
          </FocusLock>
        </div>
      )}

      {showSignOutConfirm && <SignOutConfirm onCancel={() => setShowSignOutConfirm(false)} />}
      {MOBILE_NAV.map((item) => {
        const active = item.match(pathname)
        return (
          <Link
            key={item.to}
            to={item.to}
            aria-label={item.label}
            aria-current={active ? 'page' : undefined}
            className={cn('flex-1 flex flex-col items-center justify-center gap-1 text-[11px] transition-colors duration-150 cursor-pointer', active ? 'font-semibold' : 'font-medium')}
            style={{ height: 56, color: active ? 'var(--accent-deep)' : 'var(--muted-2)' }}
          >
            {item.icon}
            {item.label}
          </Link>
        )
      })}
      <button
        type="button"
        onClick={() => setMoreOpen((v) => !v)}
        aria-haspopup="dialog"
        aria-expanded={moreOpen}
        aria-label="More"
        className={cn('flex-1 flex flex-col items-center justify-center gap-1 text-[11px] transition-colors duration-150 cursor-pointer', moreActive || moreOpen ? 'font-semibold' : 'font-medium')}
        style={{ height: 56, color: moreActive || moreOpen ? 'var(--accent-deep)' : 'var(--muted-2)' }}
      >
        <IoEllipsisHorizontal size={22} />
        More
      </button>
    </nav>
  )
}

// Kept as a default export so any remaining `import Sidebar from './Sidebar'`
// keeps working — it just renders the rail now.
export default function Sidebar() {
  return <Rail />
}
